/**
 * The **Arena low-HP danger state** (hit-feedback PRD #430 Slice D, ADR-0227):
 * one pure decision, three outputs. {@link lowHpState} classifies the runner's
 * replicated health against {@link ARENA_LOW_HP_FRACTION}, and that single
 * verdict drives - via a `data-danger` attribute flip - (1) the CSS pulse on
 * `#arena-hud-health-fill` (see {@link ./arenaHud.initArenaHud}), (2) the hurt
 * vignette's breathing baseline (see {@link ./arenaWarning}'s `setDanger`), and
 * (3) the throttled heartbeat cue owned by the machine here.
 *
 * The heartbeat follows the **storm-cue edge discipline** (ADR-0152: seed
 * silent, act on transitions): a genuine none -> danger flip beats immediately
 * - the sting that says "you just crossed the line" - but a first observation
 * that is *already* danger (a reconnect landing mid-emergency) arms the normal
 * cadence without an instant phantom pulse. While in danger the beat repeats on
 * a fixed {@link ARENA_HEARTBEAT_THROTTLE_MS} throttle with the volume ramping
 * as HP falls ({@link heartbeatVolume}); leaving danger (heal above the
 * threshold, run end, death) stops it cold. The throttle is **never faster** -
 * a re-entry inherits any window a prior beat armed, so a runner oscillating
 * across the threshold (regen out, bite back in, every push) still hears one
 * beat per window, while a re-entry after a full quiet window beats at once.
 *
 * Pure and clock-injected (the {@link ./render/screenShake.ScreenShake}
 * discipline): no DOM, no audio, no timers - `main.ts` feeds it the owner-only
 * {@link ./net/protocol.ArenaRunStatusMessage} pushes and polls {@link
 * ArenaDangerHandle.beat} from the render loop, playing the returned volume
 * through {@link ./audio/arenaCue.arenaHeartbeatCue}. Everything derives from
 * state the client already has - zero sim/wire/protocol changes.
 */

/** The runner is "dying" at or under this fraction of max HP. */
export const ARENA_LOW_HP_FRACTION = 0.3;

/** The heartbeat's fixed cadence (ms): never faster, no matter how hurt. */
export const ARENA_HEARTBEAT_THROTTLE_MS = 1500;

/** The heartbeat's volume right at the threshold - present, not yet loud. */
export const ARENA_HEARTBEAT_MIN_VOLUME = 0.35;

/** The heartbeat's volume as HP approaches zero - the emergency at full cry. */
export const ARENA_HEARTBEAT_MAX_VOLUME = 0.9;

/** The two danger states: below the threshold everything pulses, above nothing. */
export type LowHpState = 'none' | 'danger';

/**
 * The one pure decision: `danger` iff alive and at or under
 * {@link ARENA_LOW_HP_FRACTION} of max. Compared as `current <= max * fraction`
 * (never `current / max <= fraction`) so the threshold is exact where the
 * division is not float-representable (3/10 floats *above* 0.3). Dead (`<= 0`)
 * and garbage inputs are `none` - death releases the channel (the run-end push
 * follows anyway), and bad values must never pulse.
 */
export function lowHpState(current: number, max: number): LowHpState {
  if (!Number.isFinite(current) || !Number.isFinite(max)) return 'none';
  if (max <= 0 || current <= 0) return 'none';
  return current <= max * ARENA_LOW_HP_FRACTION ? 'danger' : 'none';
}

/**
 * Fraction of the way to the next level at (or past) which the XP bar lights its
 * near-level glow (PRD #431 Slice D). 90%: close enough that an orb pile pouring
 * in will visibly close the gap and telegraph the coming ding.
 */
export const ARENA_XP_NEAR_FRACTION = 0.9;

/** The two XP-bar states: `near` once the fill crosses
 * {@link ARENA_XP_NEAR_FRACTION}, `normal` below (and on a fresh rollover). */
export type XpBarState = 'normal' | 'near';

/**
 * The one pure decision for the near-level glow: `near` iff the runner is at or
 * past {@link ARENA_XP_NEAR_FRACTION} of the XP needed for the next level.
 * Compared as `xp >= toNext * fraction` (the {@link lowHpState} exactness rule).
 * Resets to `normal` on rollover for free - a level-up zeroes `xp`, so the very
 * next push reads well below the threshold. Garbage / non-positive `toNext` is
 * `normal` (never glow on bad input).
 */
export function xpBarState(xp: number, toNext: number): XpBarState {
  if (!Number.isFinite(xp) || !Number.isFinite(toNext) || toNext <= 0) return 'normal';
  return xp >= toNext * ARENA_XP_NEAR_FRACTION ? 'near' : 'normal';
}

/**
 * The heartbeat's volume for the given health: {@link ARENA_HEARTBEAT_MIN_VOLUME}
 * at the danger threshold, ramping linearly to {@link ARENA_HEARTBEAT_MAX_VOLUME}
 * as HP falls to zero; clamped so out-of-band input stays a legal gain. Note
 * the live domain is effectively `(0, 0.3 * max]`: {@link lowHpState} reports
 * `none` at `current <= 0`, so death releases the channel before `beat()` can
 * ever read hp 0 - the max-volume endpoint here is the ramp's mathematical
 * anchor (and a defensive clamp), not a "dead man's heartbeat" case to fix.
 */
export function heartbeatVolume(current: number, max: number): number {
  const fraction = !Number.isFinite(current) || !Number.isFinite(max) || max <= 0
    ? ARENA_LOW_HP_FRACTION
    : Math.min(ARENA_LOW_HP_FRACTION, Math.max(0, current / max));
  const depth = 1 - fraction / ARENA_LOW_HP_FRACTION; // 0 at the threshold -> 1 at dead
  return ARENA_HEARTBEAT_MIN_VOLUME + depth * (ARENA_HEARTBEAT_MAX_VOLUME - ARENA_HEARTBEAT_MIN_VOLUME);
}

/** Options for {@link createArenaDanger}; injected by tests, defaulted live. */
export interface ArenaDangerOptions {
  /** Wall-clock source for the throttle, defaulting to `performance.now()`. */
  now?: () => number;
}

/** The handle `main.ts` drives the danger machine through. */
export interface ArenaDangerHandle {
  /**
   * Adopt the latest status push's health and return its {@link lowHpState}.
   * Edge-triggered internally: a true none -> danger transition arms an
   * immediate beat, a first-ever (seed) observation arms only the cadence.
   */
  observe(current: number, max: number): LowHpState;
  /**
   * Poll for a due heartbeat (each render frame): returns the ramped volume
   * when one should sound now - re-arming the throttle - or `null` while
   * out of danger or inside the throttle window.
   */
  beat(): number | null;
  /** Back to the unseeded state - the every-Welcome reconnect guard. */
  reset(): void;
}

/** Wire a fresh danger machine (one per client; reset on every Welcome). */
export function createArenaDanger(opts: ArenaDangerOptions = {}): ArenaDangerHandle {
  const now = opts.now ?? (() => performance.now());

  /** Last verdict; `undefined` = unseeded (the next observe adopts silently). */
  let danger: boolean | undefined;
  /** The last observed health, for the beat's volume ramp. */
  let current = 0;
  let max = 0;
  /** The instant the next beat may sound (only meaningful while in danger). */
  let nextBeatAt = 0;

  return {
    observe(hp, maxHp) {
      const state = lowHpState(hp, maxHp);
      current = hp;
      max = maxHp;
      const inDanger = state === 'danger';
      if (inDanger && danger !== true) {
        // Entering danger. A true transition beats immediately - but never
        // earlier than a throttle already armed by a prior beat: a runner
        // flickering across the line (regen tick to 31%, bite back to 30%,
        // every push) must not defeat the fixed cadence, while a genuine
        // re-entry after a full quiet window still beats at once. A seed (the
        // storm-cue discipline - a reconnect landing mid-danger) starts the
        // cadence one full throttle out instead of stinging a phantom pulse.
        nextBeatAt =
          danger === undefined
            ? now() + ARENA_HEARTBEAT_THROTTLE_MS
            : Math.max(nextBeatAt, now());
      }
      danger = inDanger;
      return state;
    },
    beat() {
      if (danger !== true) return null;
      const at = now();
      if (at < nextBeatAt) return null;
      nextBeatAt = at + ARENA_HEARTBEAT_THROTTLE_MS;
      return heartbeatVolume(current, max);
    },
    reset() {
      danger = undefined;
      current = 0;
      max = 0;
      nextBeatAt = 0;
    },
  };
}
